import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Mail, Plus, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from './shared';

/**
 * Admin Settings → manage who receives system notification emails such as
 * "New Member Registration" and "New Order Placed". Defaults to
 * info / richard / andre @ justdive.co.za but is editable here.
 */
const NotificationRecipientsCard = ({ token }) => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/notification-recipients`, { headers });
      setRecipients(res.data.recipients || []);
    } catch (e) {
      toast.error('Failed to load notification recipients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecipients(); }, []); // eslint-disable-line

  const handleAdd = async () => {
    const email = adding.trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/admin/notification-recipients/add`,
        { email },
        { headers },
      );
      setRecipients(res.data.recipients || []);
      if (res.data.added) {
        toast.success(`Added ${email}`);
      } else {
        toast.info(`${email} is already in the list`);
      }
      setAdding('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add recipient');
    }
  };

  const handleDelete = async (email) => {
    if (recipients.length <= 1) {
      toast.error('At least one recipient must remain');
      return;
    }
    if (!window.confirm(`Remove ${email} from notification recipients?`)) return;
    try {
      const res = await axios.delete(
        `${API_URL}/admin/notification-recipients/${encodeURIComponent(email)}`,
        { headers },
      );
      setRecipients(res.data.recipients || []);
      toast.success(`Removed ${email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove recipient');
    }
  };

  return (
    <div className="bg-white rounded-lg border p-6" data-testid="notification-recipients-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-[#FF6B35]" />
          <div>
            <h3 className="font-semibold text-[#0A2342]">Notification Recipients</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              These addresses receive system-generated notifications: <strong>new member registrations</strong>, <strong>new orders</strong>, and other admin alerts. Add or remove people as your team changes.
            </p>
          </div>
        </div>
        <Button onClick={fetchRecipients} variant="outline" size="sm" data-testid="refresh-recipients-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-2 mb-4">
        {loading ? (
          <p className="text-sm text-gray-400 italic">Loading…</p>
        ) : recipients.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No recipients configured. Add at least one below.</p>
        ) : (
          recipients.map((email) => (
            <div
              key={email}
              className="flex items-center justify-between bg-gray-50 border rounded-md px-3 py-2"
              data-testid={`recipient-row-${email}`}
            >
              <span className="text-sm font-medium text-[#0A2342]">{email}</span>
              <Button
                size="sm"
                variant="ghost"
                className="text-red-600 h-7 px-2"
                onClick={() => handleDelete(email)}
                data-testid={`remove-recipient-${email}`}
                disabled={recipients.length <= 1}
                title={recipients.length <= 1 ? 'At least one recipient must remain' : 'Remove'}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      <div className="flex gap-2">
        <Input
          placeholder="someone@justdive.co.za"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          data-testid="add-recipient-input"
          className="flex-1"
        />
        <Button onClick={handleAdd} data-testid="add-recipient-btn">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
};

export default NotificationRecipientsCard;
